import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { AlertTriangle, EyeOff, FileDown, X } from "lucide-react";

import { Card } from "@/components/ui/Card";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import CopyId from "@/components/ui/CopyId";
import Select from "@/components/ui/Select";
import StatusBadge from "@/components/shared/StatusBadge";
import { CenteredSpinner } from "@/components/shared/Loader";
import { formatDate } from "@/utils/dateUtils";
import { RecordService, readBlobError, triggerBlobDownload } from "@/services/recordService";
import SectionTable from "./SectionTable";
import ExportDialog from "./ExportDialog";
import { cn } from "@/lib/utils";

/**
 * The dossier: left section nav with counts (the `candidate-analysis`
 * layout), right the selected section's table. Header carries the subject's
 * name and id, the deleted badge if any, the hidden-applications notice, the
 * application scope chip, and the Export button.
 */
const Dossier = ({
  subjectType,
  subjectId,
  applicationId,
  tenantId,
  basePath,
  canExport = false,
  section,
  onSectionChange,
  page,
  onPageChange,
  perPage,
  onPerPageChange,
  onRemoveScope,
}) => {
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [decisionExporting, setDecisionExporting] = useState(false);
  // The section responses carry the hidden-application count too, and they
  // arrive after the dossier index — so the notice tracks the freshest value.
  const [sectionHiddenCount, setSectionHiddenCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDossier(null);
    setSectionHiddenCount(null);

    const load = async () => {
      try {
        const data = await RecordService.getDossier({
          subjectType,
          subjectId,
          applicationId,
          tenantId,
        });
        if (cancelled) return;
        setDossier(data);
      } catch (err) {
        if (cancelled) return;
        setError(err);
        console.error("Failed to load record dossier:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();

    return () => {
      cancelled = true;
    };
  }, [subjectType, subjectId, applicationId, tenantId]);

  const core = dossier?.core ?? null;
  const sections = dossier?.sections ?? [];
  const hiddenApplicationCount = sectionHiddenCount ?? dossier?.hiddenApplicationCount ?? 0;

  // Default to the first section the backend returned; keep a stale section
  // key from the URL from rendering nothing.
  const activeSection = sections.find((s) => s.key === section) ?? sections[0];

  useEffect(() => {
    if (activeSection && activeSection.key !== section) {
      onSectionChange(activeSection.key);
    }
  }, [activeSection, section, onSectionChange]);

  const subjectLabel = subjectType === "user" ? "User" : "Candidate";

  // An ai_decisions row's action: Export just this — one section, internal
  // preset. The result is a bundle, not a bare PDF: `service.export_candidate`
  // returns a zip whenever anything is attached, and an ai_decisions export
  // always carries its transcripts. So this downloads; it does not preview.
  const handleExportDecision = useCallback(async () => {
    if (decisionExporting) return;
    setDecisionExporting(true);
    try {
      const { blob, filename } = await RecordService.exportBundle({
        subjectType,
        subjectId,
        params: {
          sections: "ai_decisions",
          preset: "internal",
          maskNames: "false",
          maskPii: "false",
          includeFiles: "false",
        },
        tenantId,
      });
      triggerBlobDownload(blob, filename);
      toast.success("The download contains the full decision record.");
    } catch (err) {
      const status = err?.response?.status;
      const data = await readBlobError(err);
      if (status === 429 || data?.error_code === "RATE_LIMIT_EXCEEDED") {
        toast.error("Export limit reached (5 per minute). Wait a moment and try again.");
      } else if (status === 413 || data?.error_code === "RECORD_EXPORT_TOO_LARGE") {
        toast.error(data?.message || "This export is too large (over 200 MB).");
      } else if (data?.error_code === "INSUFFICIENT_PERMISSIONS") {
        toast.error(data?.message || "You don't have permission to export records.");
      } else {
        toast.error(data?.message || "This decision could not be exported.");
      }
    } finally {
      setDecisionExporting(false);
    }
  }, [subjectType, subjectId, tenantId, decisionExporting]);

  const sectionCounts = useMemo(() => Object.fromEntries(sections.map((s) => [s.key, s.count ?? 0])), [sections]);

  if (loading) {
    return (
      <Card className="w-full rounded-xl border border-border-primary bg-background-secondary p-0">
        <div className="flex items-center justify-between border-b border-border-primary px-4 py-3">
          <div className="h-4 w-48 animate-pulse rounded bg-background-tertiary" />
          <div className="h-8 w-28 animate-pulse rounded-lg bg-background-tertiary" />
        </div>
        <CenteredSpinner className="h-64" />
      </Card>
    );
  }

  if (error) {
    const status = error?.response?.status;
    const data = error?.response?.data ?? {};
    const message =
      status === 404 ? data?.message || `No ${subjectLabel.toLowerCase()} record was found for this id.` : data?.message || "This record could not be loaded.";
    return (
      <Card className="w-full rounded-xl border border-border-primary bg-background-secondary px-6 py-10 text-center">
        <AlertTriangle size={20} className="mx-auto text-tone-amber" aria-hidden="true" />
        <p className="mt-2 text-[14px] font-medium text-text-primary">{message}</p>
        <p className="mt-1 text-[12px] text-text-secondary">Check the id and try again.</p>
      </Card>
    );
  }

  // Name keys before the email, because the two subjects spell the name
  // differently: `candidate_identity` sends `full_name` and `user_identity`
  // sends `name` (`record/repository.py`). With `name` missing from this
  // list, every employee record was headed by their email address — support
  // opens this screen to answer a question about a named person (I1768 R7).
  const name = core?.fullName || core?.name || core?.displayName || core?.email || subjectLabel;

  return (
    <div className="flex w-full flex-col gap-3">
      {hiddenApplicationCount > 0 && (
        <div className="flex items-start gap-2.5 rounded-lg border border-tone-amber/30 bg-tone-amber-soft px-3.5 py-2.5">
          <EyeOff size={15} className="mt-0.5 shrink-0 text-tone-amber" aria-hidden="true" />
          <p className="text-[12px] leading-5 text-text-primary">
            {hiddenApplicationCount} {hiddenApplicationCount === 1 ? "application is" : "applications are"} hidden by your organisation's data-visibility setting. An
            administrator can see the complete record.
          </p>
        </div>
      )}

      <div className="rounded-xl border border-border-primary bg-background-secondary px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
            <h2 className="text-[17px] font-semibold text-text-primary">{name}</h2>

            <CopyId label={subjectLabel} value={subjectId} />

            {core?.deleted && <StatusBadge isActive={false} isDeleted size="sm" />}

            {core?.createdAt && <span className="text-[12px] text-text-secondary">Since {formatDate(core.createdAt)}</span>}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {canExport && (
              <Button
                type="button"
                variant="default"
                onClick={() => setExportOpen(true)}
                className="!h-9 !min-h-0 !rounded-full !px-4 !py-0 gap-1.5 text-[12px] font-medium"
              >
                <FileDown size={13} aria-hidden="true" />
                <span>Export</span>
              </Button>
            )}
          </div>
        </div>

        {applicationId && (
          <Button
            type="button"
            variant="ghost"
            onClick={onRemoveScope}
            className="mt-2.5 !h-auto !min-h-0 items-center gap-1.5 !rounded-full border border-tone-blue/40 bg-tone-blue/10 !px-3 !py-1 text-[11px] font-medium text-text-primary hover:bg-tone-blue/20 hover:text-text-primary"
            aria-label="Remove the application scope"
            title="Remove the application scope"
          >
            Scoped to one application
            <X size={12} aria-hidden="true" />
          </Button>
        )}
      </div>

      {/* Below md the nav Card is hidden, so the same choice is a select. */}
      {sections.length > 0 && (
        <div className="md:hidden">
          <label htmlFor="record-section-select" className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-text-muted">
            {subjectLabel} record
          </label>

          <Select
            id="record-section-select"
            value={activeSection?.key ?? ""}
            onChange={onSectionChange}
            options={sections.map((s) => ({
              value: s.key,
              label: `${s.label} (${sectionCounts[s.key] ?? 0})`,
            }))}
          />
        </div>
      )}

      <div className="flex w-full items-start gap-4">
        <Card className="w-[280px] shrink-0 rounded-xl border border-border-primary bg-background-secondary p-0 md:block hidden">
          <p className="border-b border-border-primary px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-text-muted">{subjectLabel} record</p>
          <nav aria-label="Record sections" className="flex flex-col p-2">
            {sections.map((s) => {
              const active = s.key === activeSection?.key;
              return (
                <Button
                  key={s.key}
                  type="button"
                  variant="ghost"
                  onClick={() => onSectionChange(s.key)}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "!h-auto w-full !justify-between gap-2 !rounded-lg !px-3 !py-2 text-left text-[12.5px] font-normal",
                    active
                      ? "bg-button-primary text-text-tertiary hover:bg-button-primary hover:text-text-tertiary"
                      : "text-text-primary hover:bg-background-tertiary hover:text-text-primary",
                  )}
                >
                  <span className="truncate">{s.label}</span>
                  <span
                    className={cn(
                      "rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
                      active ? "bg-text-tertiary/15 text-text-tertiary" : "bg-background-tertiary text-text-secondary",
                    )}
                  >
                    {sectionCounts[s.key] ?? 0}
                  </span>
                </Button>
              );
            })}
          </nav>
        </Card>

        <div className="flex min-w-0 flex-1 flex-col">
          {activeSection && (
            <SectionTable
              key={`${subjectType}-${subjectId}-${activeSection.key}`}
              subjectType={subjectType}
              subjectId={subjectId}
              sectionKey={activeSection.key}
              page={page}
              perPage={perPage}
              onPageChange={onPageChange}
              onPerPageChange={onPerPageChange}
              applicationId={applicationId}
              tenantId={tenantId}
              basePath={basePath}
              canExport={canExport}
              onExportPdf={handleExportDecision}
              exportingDecision={decisionExporting}
              onHiddenApplicationCount={setSectionHiddenCount}
            />
          )}
        </div>
      </div>

      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        subjectType={subjectType}
        subjectId={subjectId}
        sections={sections}
        applicationId={applicationId}
        tenantId={tenantId}
      />
    </div>
  );
};

export default Dossier;
